import { ArrowUpRight, FileText, Mail, Server, Target, Linkedin, MapPin, Phone } from "lucide-react";
import PageHero from "@/components/PageHero";
import XLogo from "@/components/XLogo";
import { Reveal, FadeIn } from "@/components/Reveal";
import Seo from "@/components/Seo";
import { CONTACT_EMAIL, PHONE_DISPLAY, PHONE_TEL, SOCIAL_LINKS, OFFICES, mailto } from "@/data/site";

const TELL_US = [
  { icon: FileText, label: "Your project" },
  { icon: Server, label: "Your current environment" },
  { icon: Target, label: "The challenges you want to address" },
];

const Contact = () => (
  <div data-testid="contact-page">
    <Seo
      title="Contact TecKube — Let's Discuss What to Build or Improve"
      description="Tell TecKube about your project, your current environment and the challenges you want to address. Email info@teckube.io or call +1 (833) 832-5823."
    />
    <PageHero
      eyebrow="Contact TecKube"
      title="Let's discuss what you need to build or improve."
      lead="Tell us about your project, your current environment and the challenges you want to address."
    />

    <section className="bg-paper pb-24 md:pb-32" data-testid="contact-body">
      <div className="mx-auto max-w-7xl px-6">
        <div className="grid gap-14 md:grid-cols-12">
          <div className="md:col-span-7">
            <Reveal>
              <h2 className="font-display text-2xl font-semibold tracking-tight text-ink md:text-3xl">
                Write to us
              </h2>
              <p className="mt-5 max-w-lg text-[17px] leading-relaxed text-soot md:text-lg">
                Send us an email with a short description of what you want to build or improve —
                we&rsquo;ll take it from there. Prefer to talk? Call us.
              </p>
              <div className="mt-10">
                <a
                  href={mailto("TecKube — new project")}
                  data-testid="contact-mailto-button"
                  className="btn btn-primary px-8 py-4 text-base"
                >
                  Contact TecKube
                  <ArrowUpRight className="h-5 w-5" />
                </a>
              </div>
              <div className="mt-7 flex flex-col items-start gap-3">
                <a
                  href={mailto("TecKube — new project")}
                  data-testid="contact-email-link"
                  className="inline-flex items-center gap-2 font-mono text-[15px] text-soot underline decoration-slate-300 underline-offset-4 transition-colors duration-300 hover:text-primary hover:decoration-primary"
                >
                  <Mail className="h-4 w-4" />
                  {CONTACT_EMAIL}
                </a>
                <a
                  href={`tel:${PHONE_TEL}`}
                  data-testid="contact-phone-link"
                  className="inline-flex items-center gap-2 font-mono text-[15px] text-soot underline decoration-slate-300 underline-offset-4 transition-colors duration-300 hover:text-primary hover:decoration-primary"
                >
                  <Phone className="h-4 w-4" />
                  {PHONE_DISPLAY}
                </a>
              </div>
              <div className="mt-10 flex flex-wrap items-center gap-4">
                <span className="text-sm uppercase tracking-[0.2em] text-soot">Find us online</span>
                {SOCIAL_LINKS.map((s) => (
                  <a
                    key={s.id}
                    href={s.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`contact-${s.id}-link`}
                    aria-label={`TecKube on ${s.label}`}
                    className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-ink transition-all duration-300 hover:-translate-y-0.5 hover:border-primary hover:text-primary"
                  >
                    {s.id === "linkedin" ? <Linkedin className="h-4 w-4" /> : <XLogo className="h-3 w-3" />}
                    {s.id === "linkedin" ? "LinkedIn" : s.handle}
                  </a>
                ))}
              </div>
            </Reveal>
          </div>

          <div className="md:col-span-4 md:col-start-9">
            <Reveal delay={0.12}>
              <div
                className="noise rounded-3xl bg-night p-8 text-white md:p-10"
                data-testid="contact-include-card"
              >
                <p className="text-[11px] font-medium uppercase tracking-[0.28em] text-white/50">
                  Helpful to include
                </p>
                <ul className="mt-7 space-y-6">
                  {TELL_US.map(({ icon: Icon, label }) => (
                    <li key={label} className="flex items-start gap-4">
                      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white/10">
                        <Icon className="h-4 w-4 text-primary-soft" />
                      </span>
                      <span className="pt-2 font-display text-[15px] font-medium">{label}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>

    <section className="bg-white py-24 md:py-32" data-testid="locations-section">
      <div className="mx-auto max-w-7xl px-6">
        <Reveal>
          <span className="eyebrow">Our locations</span>
          <h2 className="mt-7 font-display text-[clamp(1.9rem,3.4vw,3rem)] font-semibold leading-[1.06] tracking-tight text-ink">
            Visit our offices
          </h2>
          <p className="mt-5 text-lg text-soot">Find us at these locations.</p>
        </Reveal>
        <div className="mt-14 grid gap-5 md:grid-cols-3">
          {OFFICES.map((office, i) => (
            <Reveal key={office.id} delay={i * 0.08}>
              <div
                className="spotlight flex h-full flex-col rounded-3xl border border-slate-200 bg-paper p-8 transition-[transform,box-shadow] duration-500 hover:-translate-y-1 hover:shadow-[0_30px_60px_-30px_rgba(15,23,42,0.25)]"
                data-testid={`office-card-${office.id}`}
                onMouseMove={(e) => {
                  const r = e.currentTarget.getBoundingClientRect();
                  e.currentTarget.style.setProperty("--mx", `${e.clientX - r.left}px`);
                  e.currentTarget.style.setProperty("--my", `${e.clientY - r.top}px`);
                }}
              >
                <div className="flex items-center justify-between">
                  <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/10">
                    <MapPin className="h-5 w-5 text-primary" />
                  </span>
                  {office.hq && (
                    <span
                      className="rounded-full bg-primary/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.2em] text-primary"
                      data-testid="office-hq-badge"
                    >
                      HQ
                    </span>
                  )}
                </div>
                <h3 className="mt-6 font-display text-xl font-semibold tracking-tight text-ink">
                  {office.country}
                </h3>
                <address className="mt-3 not-italic text-[15px] leading-relaxed text-soot">
                  {office.lines.map((line) => (
                    <span key={line} className="block">
                      {line}
                    </span>
                  ))}
                </address>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  </div>
);

export default Contact;
